import { useEffect, useRef } from 'react';
import { subscribePush, unsubscribePush } from '@/api';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Active les notifications push pour l'utilisateur connecte (tous roles) —
// best-effort silencieux : navigateur non supporte, permission refusee ou
// erreur reseau ne doivent jamais bloquer le reste de l'app.
export function usePushNotifications(isAuthenticated: boolean) {
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !VAPID_PUBLIC_KEY) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;

    (async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');

        if (Notification.permission === 'denied') return;
        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') return;
        }

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
        }

        await subscribePush(subscription.toJSON());
      } catch {
        // silencieux : les notifications push sont une amelioration, pas une dependance critique
      }
    })();
  }, [isAuthenticated]);
}

export async function disablePushNotifications() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      await unsubscribePush(subscription.endpoint);
      await subscription.unsubscribe();
    }
  } catch {
    // silencieux : la deconnexion locale ne doit jamais echouer a cause du push
  }
}
